"use client";

import { faTimes, faUserHeadset } from "@fortawesome/pro-solid-svg-icons";
import {
	type AnchorVariantProps,
	Button,
	type ButtonProps,
	cn,
	useServiceContext,
} from "@tosize/ui";
import { Slot } from "radix-ui";
import type { ComponentProps, ReactNode } from "react";
import { ShortLinkButton } from "~/app/[lang]/overlay/service/ShortLinkButton";

interface ChatShortLinkProps {
	title: string;
	fallBack: ReactNode;
}

export function ChatShortLink({ title, fallBack }: ChatShortLinkProps) {
	const { isLoading, chatHandler, chatAvailable } = useServiceContext();

	if (!chatAvailable) {
		return fallBack;
	}

	return (
		<ShortLinkButton
			component="button"
			title={title}
			loading={isLoading}
			onClick={() => chatHandler()}
		/>
	);
}

export function ChatNativeButton({ className, ...props }: ComponentProps<"button">) {
	const { chatHandler, chatAvailable } = useServiceContext();

	if (!chatAvailable) {
		return null;
	}

	return (
		<button
			type="button"
			className={cn(className, "appearance-none")}
			onClick={() => chatHandler()}
			{...props}
		/>
	);
}

interface Props extends Pick<ButtonProps, "className" | "children"> {
	iconAlign?: "start" | "end";
	color?: NonNullable<AnchorVariantProps["color"]>;
}

export function ServiceButton({ iconAlign = "start", children, ...props }: Props) {
	const { isLoading, onOpenHandler, chatAvailable, chatActive } = useServiceContext();

	if (!chatAvailable) {
		return null;
	}

	const icon = !chatActive ? faUserHeadset : faTimes;

	return (
		<Button
			variant="link"
			isLoading={isLoading}
			onClick={() => onOpenHandler()}
			starticon={iconAlign === "start" ? icon : undefined}
			endicon={iconAlign === "end" ? icon : undefined}
			{...props}
		>
			{children}
		</Button>
	);
}

export function ChatServiceSlot({ children }: { children: ReactNode }) {
	const { chatAvailable, onOpenHandler, chatHandler } = useServiceContext();

	if (!chatAvailable) {
		return null;
	}

	return (
		<Slot.Root
			onClick={async () => {
				onOpenHandler();
				await chatHandler();
			}}
		>
			{children}
		</Slot.Root>
	);
}

export function ServicePopupSlot({ children }: { children: ReactNode }) {
	const { chatAvailable, onOpenHandler } = useServiceContext();

	return (
		<Slot.Root onClick={chatAvailable ? () => onOpenHandler() : undefined}>{children}</Slot.Root>
	);
}
